import { useState, useEffect } from "react";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import UseScrollAnimation from "../../Elements/ScrollAnimation";

// ✅ VALIDATION SCHEMA
const contactSchema = z.object({
  name: z.string().trim().min(1, "Please enter your name").max(100),
  email: z.string().trim().email("Please enter a valid email").max(255),
  phone: z.string().trim().max(30).optional().or(z.literal("")),
  subject: z.string().trim().max(200).optional().or(z.literal("")),
  message: z.string().trim().min(1, "Please add a message").max(5000),
});

// ✅ SINGLE SOURCE OF TRUTH
const initialFormData = {
  name: "",
  email: "",
  phone: "",
  message: "",
  _id: "",
  subject: "",
};

function FormContactUS() {
  UseScrollAnimation();

  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(initialFormData);

  // ✅ GENERATE UNIQUE ID
  function generateId() {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.toLocaleString("en-US", { month: "short" }).toUpperCase();
    const date = String(now.getDate()).padStart(2, "0");
    const day = now.toLocaleString("en-US", { weekday: "short" }).toUpperCase();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");

    return `${year}${month}${date}${day}${hours}${minutes}${seconds}`;
  }

  // ✅ SET INITIAL ID
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      _id: generateId(),
    }));
  }, []);

  // ✅ HANDLE INPUT CHANGE
  function handleChange(event) {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  // ✅ SUBMIT FUNCTION
  const onSubmit = async (e) => {
    e.preventDefault();

    const parsed = contactSchema.safeParse(formData);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Please check the form");
      return;
    }

    setSubmitting(true);

    try {
      const data = new FormData();
      for (const key in formData) {
        data.append(key, formData[key]);
      }

      const scriptURL =
        "https://script.google.com/macros/s/AKfycbyoIGEPutAT6VM7oVIblh81uaI0vYmJRcVq82GE-70XpB4riCROg99lWesebL5c1uXz/exec";

      await fetch(scriptURL, {
        method: "POST",
        body: data,
      });

      // ✅ Toast
      toast.success("Thank you — your message has been saved and email sent!");

      // ✅ Browser Popup (NEW)
      alert(
        "Your form has been submitted successfully.\nPlease check your email for confirmation.",
      );

      // ✅ Reset form
      setFormData({
        ...initialFormData,
        _id: generateId(),
      });

      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong.");
    }

    setSubmitting(false);
  };
  return (
    <>
      <form onSubmit={onSubmit} className="ContactUs_Section_FormBox">
        <input type="hidden" name="_id" value={formData._id} />
        <div className="ContactUs_Section_FormBox_LableInputBox animate-top">
          <Field
            label="Name"
            name="name"
            required
            value={formData.name}
            onChange={handleChange}
          />
          <Field
            label="Email"
            name="email"
            type="email"
            required
            value={formData.email}
            onChange={handleChange}
          />
        </div>

        <div className="ContactUs_Section_FormBox_LableInputBox animate-top">
          <Field
            label="Phone"
            name="phone"
            type="tel"
            required
            value={formData.phone}
            onChange={handleChange}
          />
          <Field
            label="Subject"
            name="subject"
            required
            value={formData.subject}
            onChange={handleChange}
          />
        </div>

        <div className="ContactUs_Section_FormBox_LableTextarea">
          <label className="ContactUs_Section_FormBox_Lable animate-top">
            Message
          </label>
          <textarea
            name="message"
            required
            rows={6}
            maxLength={5000}
            value={formData.message}
            onChange={handleChange}
            placeholder="Tell me a little about what brings you here…"
            className="ContactUs_Section_FormBox_Textarea animate-top"
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="ContactUs_Section_Button01"
        >
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
          {submitting ? "Sending…" : "Send message"}
        </button>
        <span className="ContactUs_Section_FormBox_BottomContent">
          Your information is private and never shared.
        </span>
      </form>
    </>
  );
}

// ✅ UPDATED FIELD COMPONENT (CONTROLLED)
const Field = ({ label, name, type = "text", required, value, onChange }) => (
  <div className="ContactUs_Section_FormBox_LableInput">
    <label className="ContactUs_Section_FormBox_Lable">{label}</label>
    <input
      name={name}
      type={type}
      required={required}
      value={value}
      onChange={onChange}
      className="ContactUs_Section_FormBox_Input"
    />
  </div>
);

export default FormContactUS;
